import { useEffect, useRef, useContext, createContext, useMemo, ReactNode, useCallback } from 'react';
import { MapMouseEvent } from 'mapbox-gl';
import { useStateRef } from '@alltrails/core';
import { LatLng } from '../../../types/Geo';
import { useAppDispatch, useAppSelector } from '../../../redux';
import { selectIsDrawingEnabled, setActiveSegmentId } from '../../../redux/customRoute';
import { updateCursor } from '../../../redux/reducer';
import { customRouteActiveSegmentChevronsId, customRouteActiveSegmentPolylineId, customRouteMarkersId, osmLinesId } from '../../../utils/layers';
import { MapLayerInteractionsContext } from '../../MapLayerInteractionsProvider';
import { MarkerProperties } from '../../../types/CustomRoute';
import useSegmentActions from '../useSegmentActions';
import useRouteToPoint, { PendingHandle } from './useRouteToPoint';
import useOverrideSmartRouting from './useOverrideSmartRouting';
import useFreeDraw from './useFreeDraw';
import useDragMarker from './useDragMarker';
import useAddHandle from './useAddHandle';

export const CURSOR_DEFAULT = 'unset';
export const CURSOR_DRAWING = 'crosshair';
export const CURSOR_MARKER = 'pointer';

type DrawControlsContextType = {
  pendingHandle?: PendingHandle;
  dragMarker?: MarkerProperties;
  freeDrawPoints?: LatLng[];
  isContextMenuOpen: boolean;
  isContextMenuOpenRef: React.MutableRefObject<boolean>;
  setIsContextMenuOpen: (isOpen: boolean) => void;
};

export const DrawControlsContext = createContext<DrawControlsContextType>({
  pendingHandle: undefined,
  dragMarker: undefined,
  freeDrawPoints: undefined,
  isContextMenuOpen: false,
  isContextMenuOpenRef: { current: false },
  setIsContextMenuOpen: () => {}
});

/**
 * Keep drawing state separate from the redux store until drawing is complete. This greatly improves performance while drawing since drawing involves rapid state changes.
 * While we separate the state for each drawing feature into hooks we define all event handlers in this provider since MapLayerInteractionsProvider enforces a single event handler per event type per layer.
 * @param children components that need access to the drawing state
 * @returns a provider that wraps the children and provides the drawing state
 */
const DrawControlsProvider = ({ children }: { children: ReactNode }) => {
  const dispatch = useAppDispatch();

  // Store drawing state in refs to prevent re-registering event handlers as the state changes
  const isInMarkerRef = useRef(false);
  const [isContextMenuOpen, setIsContextMenuOpen, isContextMenuOpenRef] = useStateRef(false);

  // Note that the actual delay used for double clicks varies by device and browser so this will never work perfectly
  // Double click detection adds a lot of complexity and makes timing with animations more difficult
  // If double click detection becomes an issue we should instead disable mapbox's double click to zoom or just accept that double clicking will add two markers
  // To do so we can pass doubleClickZoom: false to the controlsConfig prop of BaseMap on the pages using this component
  const doubleClickDelay = 300;
  const clickTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const { isSmartRoutingRef, override: overrideSmartRouting, restore: restoreSmartRouting } = useOverrideSmartRouting();
  const { routeToPoint, pendingHandle } = useRouteToPoint(isSmartRoutingRef);
  const addHandle = useAddHandle();

  const {
    isStartingRef: isFreeDrawStartingRef,
    isPendingRef: isFreeDrawPendingRef,
    points: freeDrawPoints,
    start: startFreeDraw,
    update: updateFreeDraw,
    save: saveFreeDraw
  } = useFreeDraw(isSmartRoutingRef);

  const {
    isStartingRef: isDragMarkerStartingRef,
    isPendingRef: isDragMarkerPendingRef,
    pendingMarker: dragMarker,
    start: startDragMarker,
    update: updateDragMarker,
    save: saveDragMarker
  } = useDragMarker();

  const { undo, redo, removePoint } = useSegmentActions();

  const { registerEventHandler, unregisterEventHandler, registerBaseMapEventHandlers, unregisterBaseMapEventHandlers } =
    useContext(MapLayerInteractionsContext);

  // This doesn't need to be a ref since we only need event handlers when drawing is enabled
  const isDrawingEnabled = useAppSelector(selectIsDrawingEnabled);
  const { isPOISelected, cursor } = useAppSelector(state => ({
    isPOISelected: state.map.selectedPOI,
    cursor: state.map.cursor
  }));

  // Check if any drawing action is pending
  const getIsActionPending = useCallback(
    () => isFreeDrawPendingRef.current || isDragMarkerPendingRef.current,
    [isFreeDrawPendingRef, isDragMarkerPendingRef]
  );

  // Check if any drawing action is starting or pending
  const getIsActionStarting = useCallback(
    () => isFreeDrawStartingRef.current || isDragMarkerStartingRef.current || getIsActionPending(),
    [isFreeDrawStartingRef, isDragMarkerStartingRef, getIsActionPending]
  );

  const resetCursor = useCallback(() => {
    if (isInMarkerRef.current) {
      dispatch(updateCursor(CURSOR_MARKER));
    } else {
      dispatch(updateCursor(CURSOR_DEFAULT));
    }
  }, [dispatch]);

  // Update the cursor when drawing is enabled
  useEffect(() => {
    dispatch(updateCursor(isDrawingEnabled ? CURSOR_DRAWING : CURSOR_DEFAULT));
  }, [dispatch, isDrawingEnabled]);

  // Update the cursor if it's set to "" after hovering or clicking a POI
  useEffect(() => {
    if (cursor === '' && isDrawingEnabled) {
      dispatch(updateCursor(CURSOR_DRAWING));
    }
  }, [cursor, dispatch, isDrawingEnabled]);

  // Register keyboard handlers
  useEffect(() => {
    if (!isDrawingEnabled) {
      return;
    }

    // Ignore keyboard events that are triggered by text elements
    const isTextElement = (event: KeyboardEvent) => {
      const eventTarget = event.target as HTMLElement;
      return eventTarget && eventTarget.tagName && ['input', 'textarea'].includes(eventTarget.tagName.toLowerCase());
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (isTextElement(event)) {
        return;
      }

      // Take care to add key combinations from most specific to least specific
      // e.g. Ctrl+Shift+z should be handled before Ctrl+z so that redo does not trigger undo

      if (
        (event.key === 'y' && (event.ctrlKey || event.metaKey)) ||
        // On mac Cmd+Shift comes in with 'z' but Ctrl+Shift comes in with 'Z'
        ((event.key === 'z' || event.key === 'Z') && event.shiftKey && (event.ctrlKey || event.metaKey))
      ) {
        redo('keyboard');
      } else if (event.key === 'z' && (event.ctrlKey || event.metaKey)) {
        undo();
      } else if (event.key === 'Backspace' || event.key === 'Delete') {
        removePoint();
        setIsContextMenuOpen(false);
      } else if (event.key === 'Escape') {
        dispatch(setActiveSegmentId({ activeSegmentId: undefined, canEdit: true }));
      } else if (event.key === 'Shift' && !event.ctrlKey && !event.metaKey) {
        overrideSmartRouting();
      } else {
        // Don't prevent default behavior if we are not handling the event
        return;
      }

      // Prevent default key combination behavior if we are handling the event
      event.preventDefault();
    };

    const onKeyUp = (event: KeyboardEvent) => {
      if (isTextElement(event)) {
        return;
      }

      if (event.key === 'Shift') {
        saveFreeDraw();
        restoreSmartRouting();
        resetCursor();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('keyup', onKeyUp);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('keyup', onKeyUp);
    };
  }, [
    isDrawingEnabled,
    setIsContextMenuOpen,
    overrideSmartRouting,
    undo,
    redo,
    removePoint,
    resetCursor,
    restoreSmartRouting,
    saveFreeDraw,
    dispatch
  ]);

  // Register mouse handlers
  useEffect(() => {
    if (!isDrawingEnabled) {
      return;
    }

    const withoutDoubleClick = (callback: (event: MapMouseEvent) => void) => {
      return (event: MapMouseEvent) => {
        // If a click is already in progress, this is a double click and we can ignore both the previous and current clicks
        if (clickTimeoutRef.current) {
          clearTimeout(clickTimeoutRef.current);
          clickTimeoutRef.current = null;
          return;
        }

        // The features array of a MapMouseEvent is not guaranteed to exist if we wait to access it until after the timeout so we must clone the event
        // Since some properties of the event are non-enumerable we must use Object.assign instead of the spread operator to get a complete clone
        const clonedEvent = Object.assign({}, event);
        if (event.features) {
          clonedEvent.features = event.features.map(feature => Object.assign({}, feature));
        }

        clickTimeoutRef.current = setTimeout(async () => {
          // Await the callback to ensure that the timeout is not cleared until the callback has completed
          // This prevents a race condition between this callback and the callback for any clicks that occur while this one is still pending
          await callback(clonedEvent);
          clickTimeoutRef.current = null;
        }, doubleClickDelay);
      };
    };

    const onMapClickBase = (event: MapMouseEvent) => {
      if (getIsActionPending() || isContextMenuOpenRef.current || isPOISelected) {
        return;
      }

      return routeToPoint(event.lngLat);
    };
    const onMapClick = withoutDoubleClick(onMapClickBase);

    const onActivePolylineClickBase = (event: MapMouseEvent) => {
      if (getIsActionPending() || isContextMenuOpenRef.current) {
        return false;
      }

      return addHandle(event);
    };
    const onActivePolylineClick = withoutDoubleClick(onActivePolylineClickBase);

    const onOsmLineClickBase = (event: MapMouseEvent) => {
      // Attempt to treat the click as a click on the active segment polyline since OSM lines render above the active segment polyline
      const result = onActivePolylineClickBase(event);
      if (result) {
        return;
      }

      // If the click was not on the active segment polyline, treat it as a click on the base map
      onMapClickBase(event);
    };
    const onOsmLineClick = withoutDoubleClick(onOsmLineClickBase);

    const onMarkerMouseEnter = () => {
      isInMarkerRef.current = true;

      if (!getIsActionPending()) {
        dispatch(updateCursor(CURSOR_MARKER));
      }
    };

    const onMarkerMouseLeave = () => {
      if (!getIsActionPending()) {
        // This is only safe to skip while in draw mode if we are guaranteed that smart routing will always result in new paths with a marker under the cursor
        // (i.e. we append a straight line from the end of the server's smart routing result to the cursor if the result does not end at the cursor)
        // If this is not true the marker for new paths can render away from the cursor without triggering a mouseleave event
        isInMarkerRef.current = false;
        dispatch(updateCursor(CURSOR_DRAWING));
      }
    };

    const onMouseDown = (event: MapMouseEvent) => {
      if (event.originalEvent.shiftKey) {
        startFreeDraw(event);
      }
    };

    const onMarkerMouseDown = (event: MapMouseEvent) => {
      onMouseDown(event);

      if (!getIsActionStarting()) {
        startDragMarker(event);
      }
    };

    const onMouseUp = () => {
      if (getIsActionStarting()) {
        resetCursor();
      }

      saveFreeDraw();
      saveDragMarker();
    };

    const onMouseMove = (event: MapMouseEvent) => {
      updateFreeDraw(event);
      updateDragMarker(event);
    };

    registerEventHandler(osmLinesId, 'click', onOsmLineClick);
    registerEventHandler(osmLinesId, 'mousedown', onMouseDown);
    // Since chevrons render above the polyline, treat a click on a chevron as a click on the polyline
    registerEventHandler(customRouteActiveSegmentChevronsId, 'click', onActivePolylineClick);
    registerEventHandler(customRouteActiveSegmentPolylineId, 'click', onActivePolylineClick);
    registerEventHandler(customRouteMarkersId, 'mousedown', onMarkerMouseDown);
    registerEventHandler(customRouteMarkersId, 'mouseenter', onMarkerMouseEnter);
    registerEventHandler(customRouteMarkersId, 'mouseleave', onMarkerMouseLeave);
    registerBaseMapEventHandlers({
      click: { handler: onMapClick },
      mousedown: { handler: onMouseDown },
      // We want to handle drawing that started on the base map but ended on a layer
      mouseup: { handler: onMouseUp, allLayers: true },
      mousemove: { handler: onMouseMove, allLayers: true }
    });

    return () => {
      unregisterEventHandler(osmLinesId, 'click', onOsmLineClick);
      unregisterEventHandler(osmLinesId, 'mousedown', onMouseDown);
      unregisterEventHandler(customRouteActiveSegmentChevronsId, 'click', onActivePolylineClick);
      unregisterEventHandler(customRouteActiveSegmentPolylineId, 'click', onActivePolylineClick);
      unregisterEventHandler(customRouteMarkersId, 'mousedown', onMouseDown);
      unregisterEventHandler(customRouteMarkersId, 'mouseenter', onMarkerMouseEnter);
      unregisterEventHandler(customRouteMarkersId, 'mouseleave', onMarkerMouseLeave);
      unregisterBaseMapEventHandlers(['click', 'mousedown', 'mouseup', 'mousemove']);
    };
  }, [
    isDrawingEnabled,
    isInMarkerRef,
    isContextMenuOpenRef,
    getIsActionPending,
    getIsActionStarting,
    overrideSmartRouting,
    restoreSmartRouting,
    resetCursor,
    routeToPoint,
    addHandle,
    startFreeDraw,
    updateFreeDraw,
    saveFreeDraw,
    startDragMarker,
    updateDragMarker,
    saveDragMarker,
    dispatch,
    registerEventHandler,
    unregisterEventHandler,
    registerBaseMapEventHandlers,
    unregisterBaseMapEventHandlers,
    isPOISelected
  ]);

  const contextValue = useMemo(
    () => ({
      pendingHandle,
      dragMarker,
      freeDrawPoints,
      isContextMenuOpen,
      isContextMenuOpenRef,
      setIsContextMenuOpen
    }),
    [pendingHandle, dragMarker, freeDrawPoints, isContextMenuOpen, isContextMenuOpenRef, setIsContextMenuOpen]
  );

  return <DrawControlsContext.Provider value={contextValue}>{children}</DrawControlsContext.Provider>;
};

export default DrawControlsProvider;
